import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getVersions, getContracts, revertToVersion, deleteVersion, createContract } from '../api';
import VersionCard from './VersionCard';
import VersionDiffModal from './VersionDiffModal';
import Modal from './Modal';
import { RefreshCw, Search, Filter, History, ChevronRight, FileText, Calendar, Database, ArrowRightLeft } from 'lucide-react';

const VersionHistory = ({ contractId, addToast, onContractChanged, onNavigateToComparator }) => {
    // Data State
    const [contracts, setContracts] = useState([]);
    const [selectedContractId, setSelectedContractId] = useState(contractId || null);
    const [versions, setVersions] = useState([]);

    // UI State
    const [loadingContracts, setLoadingContracts] = useState(false);
    const [loadingVersions, setLoadingVersions] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Modals state
    const [diffVersion, setDiffVersion] = useState(null);
    const [revertVersion, setRevertVersion] = useState(null);

    // Undo grace period state
    const [pendingDeletions, setPendingDeletions] = useState(new Set());
    const deletionTimeoutsRef = useRef({});

    // Selection State for Comparison
    const [selectedVersionIds, setSelectedVersionIds] = useState(new Set());

    const handleToggleSelect = (version) => {
        setSelectedVersionIds(prev => {
            const next = new Set(prev);
            if (next.has(version.id)) {
                next.delete(version.id);
            } else {
                if (next.size >= 2) {
                    // Start fresh if 2 already selected? Or just prevent?
                    // Let's replace the first one (FIFO-ish behavior on Sets is insertion order)
                    const first = next.values().next().value;
                    next.delete(first);
                }
                next.add(version.id);
            }
            return next;
        });
    };

    const handleCompareSelected = () => {
        if (selectedVersionIds.size !== 2) return;
        const selected = versions.filter(v => selectedVersionIds.has(v.id));
        if (selected.length !== 2) return;

        // Sort by version number (descending) to find new vs old
        // selected[0] should be newer (higher version), selected[1] older
        const sorted = selected.sort((a, b) => b.version_number - a.version_number);
        const newVer = sorted[0];
        const oldVer = sorted[1];

        if (onNavigateToComparator) {
            onNavigateToComparator(selectedContractId, oldVer, newVer);
        } else {
            // Fallback for modal
            setDiffVersion({ old: oldVer, new: newVer });
        }

        // Clear selection after navigation? Maybe keep it.
        // setSelectedVersionIds(new Set());
    };

    // Initial Load
    useEffect(() => {
        loadContracts();
    }, []);

    // Handle prop change
    useEffect(() => {
        if (contractId) {
            setSelectedContractId(contractId);
        }
    }, [contractId]);

    // Notify parent when contract selection changes (for cleanup)
    useEffect(() => {
        if (selectedContractId && selectedContractId !== contractId) {
            onContractChanged?.();
        }
    }, [selectedContractId, contractId, onContractChanged]);

    // Load versions when contract changes
    useEffect(() => {
        if (selectedContractId) {
            loadVersions(selectedContractId);
            setSelectedVersionIds(new Set()); // Reset selection
        } else {
            setVersions([]);
        }
    }, [selectedContractId]);

    const loadContracts = async () => {
        setLoadingContracts(true);
        try {
            const data = await getContracts();
            setContracts(data);
            // Auto-select first if none selected and not provided by prop
            if (!contractId && !selectedContractId && data.length > 0) {
                setSelectedContractId(data[0].id);
            }
        } catch (err) {
            console.error("Failed to load contracts", err);
            addToast?.("Failed to load contracts list", "error");
        } finally {
            setLoadingContracts(false);
        }
    };

    const loadVersions = async (id) => {
        setLoadingVersions(true);
        try {
            const data = await getVersions(id);
            // Sort by version number descending just in case API doesn't
            const sorted = data.sort((a, b) => b.version_number - a.version_number);
            setVersions(sorted);
        } catch (err) {
            console.error("Failed to load versions", err);
            addToast?.("Failed to load versions", "error");
        } finally {
            setLoadingVersions(false);
        }
    };

    // Actions
    // handleView removed in favor of local expansion state in card

    const handleRevertConfirm = async () => {
        if (!revertVersion || !selectedContractId) return;
        try {
            await revertToVersion(selectedContractId, revertVersion.version_number);
            addToast?.(`Reverted to version ${revertVersion.version_number}`, "success");
            setRevertVersion(null);
            loadVersions(selectedContractId);
        } catch (err) {
            console.error(err);
            addToast?.("Failed to revert version", "error");
        }
    };

    const handleDelete = useCallback((version) => {
        if (!selectedContractId) return;

        // Optimistic UI hide
        setPendingDeletions(prev => new Set(prev).add(version.id));

        const timeoutId = setTimeout(async () => {
            try {
                await deleteVersion(selectedContractId, version.version_number);
                setPendingDeletions(prev => {
                    const next = new Set(prev);
                    next.delete(version.id);
                    return next;
                });
                loadVersions(selectedContractId);
            } catch (err) {
                console.error(err);
                addToast?.("Failed to delete version", "error");
                setPendingDeletions(prev => {
                    const next = new Set(prev);
                    next.delete(version.id);
                    return next;
                });
            }
        }, 5000);

        deletionTimeoutsRef.current[version.id] = timeoutId;

        addToast?.(`Version ${version.version_number} deleted`, "success", {
            duration: 5000,
            action: {
                label: 'Undo',
                onClick: () => {
                    clearTimeout(deletionTimeoutsRef.current[version.id]);
                    delete deletionTimeoutsRef.current[version.id];
                    setPendingDeletions(prev => {
                        const next = new Set(prev);
                        next.delete(version.id);
                        return next;
                    });
                    addToast?.('Deletion undone', 'info');
                }
            }
        });
    }, [selectedContractId, addToast]); // loadVersions can be omitted if it triggers stale closures but maybe better not to, wait it's not memoized so it will cause updates. Let's not include loadVersions if it's not useCallback. Actually it's better to just leave it out or move it? loadVersions is defined below useEffect, let me just omit it since it's defined in the component.

    const handleForkFromVersion = async (version, forkName) => {
        try {
            await createContract({
                name: forkName,
                schema_content: version.schema_content,
                description: `Forked from ${selectedContract?.name || 'Contract'} v${version.version_number}`,
                tags: ['forked-from-version']
            });
            addToast?.(`Contract forked as "${forkName}"`, 'success');
            // Refresh contracts list in sidebar
            loadContracts();
        } catch (err) {
            console.error('Fork failed:', err);
            addToast?.('Failed to fork from version: ' + (err.response?.data?.detail || err.message), 'error');
        }
    };

    // Filtered contracts
    const filteredContracts = contracts.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedContract = contracts.find(c => c.id === Number(selectedContractId));

    return (
        <div className="flex h-full bg-[#1e1e1e] overflow-hidden">
            {/* Sidebar (Contract List) - Hidden on mobile unless toggled */}
            {!contractId && (
                <div className={`
                    absolute md:relative inset-0 z-20 md:z-0 flex flex-col w-full md:w-80 bg-[#252526] border-r border-[#3c3c3c] transition-transform duration-300
                    ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
                `}>
                    <div className="p-4 border-b border-[#3c3c3c]">
                        <h2 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                            <Database size={18} className="text-blue-500" />
                            Contracts
                        </h2>
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input
                                type="text"
                                placeholder="Search contracts..."
                                className="w-full pl-9 pr-4 py-2 bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 text-white"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {loadingContracts ? (
                            <div className="flex justify-center py-8"><RefreshCw className="animate-spin text-gray-400" /></div>
                        ) : filteredContracts.length === 0 ? (
                            <div className="text-center py-8 text-gray-400 text-sm">No contracts found</div>
                        ) : (
                            filteredContracts.map(contract => (
                                <button
                                    key={contract.id}
                                    onClick={() => {
                                        setSelectedContractId(contract.id);
                                        setMobileMenuOpen(false);
                                    }}
                                    className={`
                                        w-full text-left px-4 py-3 rounded-lg text-sm flex items-center justify-between group transition-colors
                                        ${Number(selectedContractId) === contract.id
                                            ? 'bg-blue-900/20 text-blue-300 border border-blue-800'
                                            : 'text-[#cccccc] hover:bg-[#3c3c3c]'}
                                    `}
                                >
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <div className={`p-2 rounded-md ${Number(selectedContractId) === contract.id ? 'bg-blue-800' : 'bg-[#333]'}`}>
                                            <FileText size={16} />
                                        </div>
                                        <div className="truncate font-medium">{contract.name}</div>
                                    </div>
                                    {Number(selectedContractId) === contract.id && <ChevronRight size={16} />}
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Main Content (Version Timeline) */}
            <div className="flex-1 flex flex-col h-full overflow-hidden w-full">
                {/* Header */}
                <header className="bg-[#252526] border-b border-[#3c3c3c] px-6 py-4 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-4">
                        {!contractId && (
                            <button
                                className="md:hidden p-2 -ml-2 text-gray-400 hover:bg-[#3c3c3c] rounded-md transition-colors"
                                onClick={() => setMobileMenuOpen(prev => !prev)}
                            >
                                <Database size={20} />
                            </button>
                        )}
                        <div>
                            <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                <History className="text-purple-500" />
                                {selectedContract ? selectedContract.name : 'Version History'}
                            </h2>
                            <p className="text-sm text-[#808080]">
                                {versions.length} versions recorded
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => selectedContractId && loadVersions(selectedContractId)}
                        className="p-2 text-gray-500 hover:bg-[#3c3c3c] rounded-lg transition-colors"
                        title="Refresh Versions"
                    >
                        <RefreshCw size={20} className={loadingVersions ? "animate-spin" : ""} />
                    </button>
                </header>

                {/* Timeline */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8 bg-[#1e1e1e] relative">
                    <div className="max-w-4xl mx-auto pb-20">
                        {!selectedContractId ? (
                            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                                <Database size={48} className="mb-4 opacity-20" />
                                <p>Select a contract to view its version history</p>
                            </div>
                        ) : loadingVersions && versions.length === 0 ? (
                            <div className="flex justify-center py-20">
                                <RefreshCw size={32} className="animate-spin text-blue-500" />
                            </div>
                        ) : versions.length === 0 ? (
                            <div className="text-center py-20 bg-[#252526] rounded-xl border border-dashed border-[#3c3c3c]">
                                <History size={32} className="mx-auto mb-3 text-gray-600" />
                                <p className="text-gray-400">No version history found.</p>
                            </div>
                        ) : (
                            <div className="relative space-y-6">
                                {/* Vertical Timeline Line */}
                                <div className="absolute left-8 top-4 bottom-4 w-0.5 bg-[#3c3c3c] -z-10"></div>

                                {versions.filter(v => !pendingDeletions.has(v.id)).map((version, index) => (
                                    <div key={version.id} className="relative pl-2 group">
                                        <VersionCard
                                            version={version}
                                            isLatest={index === 0}
                                            isDeleted={version.is_deleted}
                                            isSelected={selectedVersionIds.has(version.id)}
                                            onToggleSelect={handleToggleSelect}
                                            onDiff={() => {
                                                // Navigate to comparator if callback provided, otherwise use modal fallback
                                                if (onNavigateToComparator) {
                                                    const oldVer = index === 0 && versions.length > 1 ? versions[1] : version;
                                                    const newVer = index === 0 && versions.length > 1 ? version : versions[0];
                                                    onNavigateToComparator(selectedContractId, oldVer, newVer);
                                                } else {
                                                    // Fallback to modal behavior
                                                    if (index === 0 && versions.length > 1) {
                                                        setDiffVersion({ old: versions[1], new: version });
                                                    } else {
                                                        setDiffVersion({ old: version, new: versions[0] });
                                                    }
                                                }
                                            }}
                                            onRevert={() => setRevertVersion(version)}
                                            onDelete={() => handleDelete(version)}
                                            onFork={handleForkFromVersion}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Compare Selected FAB */}
                    {selectedVersionIds.size === 2 && (
                        <div className="fixed bottom-8 right-8 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300">
                            <button
                                onClick={handleCompareSelected}
                                className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all font-bold"
                            >
                                <ArrowRightLeft size={20} />
                                Compare 2 Selected
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <VersionDiffModal
                contractId={selectedContractId}
                oldVersion={diffVersion?.old}
                newVersion={diffVersion?.new}
                onClose={() => setDiffVersion(null)}
            />

            <Modal
                isOpen={!!revertVersion}
                onClose={() => setRevertVersion(null)}
                title="Confirm Revert"
                footer={
                    <>
                        <button onClick={() => setRevertVersion(null)} className="px-4 py-2 text-[#cccccc] hover:bg-[#3c3c3c] rounded-lg transition-colors">Cancel</button>
                        <button onClick={handleRevertConfirm} className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium">Revert to v{revertVersion?.version_number}</button>
                    </>
                }
            >
                <div className="space-y-3">
                    <p className="text-gray-200">
                        Are you sure you want to revert to <span className="font-bold text-orange-400">Version {revertVersion?.version_number}</span>?
                    </p>
                    <div className="bg-orange-900/20 border border-orange-900/50 p-3 rounded-md text-sm text-orange-200">
                        This will create a new version (v{versions.length > 0 ? versions[0].version_number + 1 : 1}) containing exactly the same data as Version {revertVersion?.version_number}. Checks will be bypassed.
                    </div>
                </div>
            </Modal>

        </div>
    );
};

export default VersionHistory;
